import { CollectionWorkbench } from './CollectionWorkbench';
import { PaymentSettings } from './PaymentSettings';
import { collaborationOnly } from '@yingbaba/utils';
import { LifecycleManagement, BusinessTimeoutSettings } from './LifecycleManagement';
import { useEffect, useRef, useState } from 'react';
import { Routes, Route, NavLink, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSession, useData, useList, api, logout, post, postIdempotent } from '@yingbaba/api';
import type { OrganizationVerificationAdminDetail } from '@yingbaba/types';
import {
  Select,
  Avatar,
  Brand,
  AuthPage,
  LoginRequired,
  IconButton,
  PageTitle,
  Stat,
  Status,
  QueryState,
  Empty,
  Button,
  ButtonLink,
  ActionDialog,
  Notice,
  ErrorNotice,
  Pagination,
  Modal,
  PrivateDocumentPreview,
} from '@yingbaba/ui';
import {
  LayoutDashboard,
  Users,
  Building2,
  ShieldCheck,
  FileText,
  BriefcaseBusiness,
  Camera,
  Image,
  Banknote,
  Receipt,
  Settings,
  Activity,
  AlertTriangle,
  LogOut,
  Menu,
  Bell,
  MessageSquare,
  ArrowUpRight,
  ArrowRight,
  Check,
  Plus,
  ChevronDown,
} from 'lucide-react';
import { money, datetime, siteURL } from '@yingbaba/utils';
import { PlatformMembers } from './PlatformMembers';
import { FinanceWorkbench } from './FinanceWorkbench';
import { BusinessEmailDeliveries } from './BusinessEmailDeliveries';
import { PaymentMerchantDialog } from './PaymentMerchantDialog';
import { CustomerService } from './CustomerService';
import { CommissionSettings } from './CommissionSettings';
import { MemberManagement } from './MemberManagement';
import { DigitalManagement } from './DigitalManagement';
import { AfterSalesWorkbench } from './AfterSalesWorkbench';
import { ReviewModeration } from './ReviewModeration';
import { PortfolioReview } from './PortfolioReview';
import { AccountsDirectory, OrganizationsDirectory, OrganizationDetail } from './Directory';
import { AdminAccount } from './AdminAccount';
type Row = {
  id: string;
  created_at: string;
  status?: string;
  name?: string;
  title?: string;
  email?: string;
  category?: string;
  city?: string;
  description?: string;
  summary?: string;
  is_sample?: boolean;
  reason?: string;
  reference?: string;
  review_reason?: string;
  provider?: string;
  external_merchant_id?: string;
  onboarding_reference?: string;
  capabilities?: string;
  payment_id?: string;
  payment_intent_id?: string;
  sub_merchant_id?: string;
  merchant_split_no?: string;
  external_split_no?: string;
  platform_amount_minor?: number;
  provider_amount_minor?: number;
  merchant_refund_no?: string;
  external_refund_no?: string;
  refund_id?: string;
  split_transaction_id?: string;
  merchant_reversal_no?: string;
  external_reversal_no?: string;
  record_type?: string;
  external_record_no?: string;
  business_date?: string;
  local_entity_id?: string;
  expected_amount_minor?: number;
  fee_amount_minor?: number;
  difference_minor?: number;
  source_reference?: string;
  resolution?: string;
  resolved_at?: string;
  rate_bps?: number;
  version?: number;
  gross_amount_minor?: number;
  amount_minor?: number;
  commission_amount_minor?: number;
  provider_receivable_minor?: number;
  action?: string;
  before?: string;
  after?: string;
  entity_id?: string;
  organization_id?: string;
  account_id?: string;
  content?: string;
  enabled?: boolean;
  key?: string;
  link?: string;
  [key: string]: unknown;
};
type AIHealthSummary = {
  window_hours: number;
  attempts: number;
  successes: number;
  failures: number;
  timeouts: number;
  avg_latency_ms: number;
  last_status: string;
  last_error_code: string;
  last_latency_ms: number;
  last_invoked_at: string | null;
};
type AIModelConfig = {
  id: string;
  provider_id: string;
  name: string;
  model: string;
  capabilities: string;
  enabled: boolean;
  priority: number;
  context_window: number;
  max_output_tokens: number;
  health: AIHealthSummary;
};
type AIProviderConfig = {
  id: string;
  name: string;
  type: string;
  base_url: string;
  api_key_configured: boolean;
  enabled: boolean;
  priority: number;
  timeout_seconds: number;
  models: AIModelConfig[];
  health: AIHealthSummary;
};
type AIDiagnosticResult = {
  ok: boolean;
  billable: boolean;
  status: string;
  category: string;
  latency_ms: number;
  error_code: string;
  message: string;
  request_id: string;
  invocation_id: string;
  provider_id: string;
  model_id: string;
};
type AIInvocationRecord = {
  id: string;
  created_at: string;
  purpose: string;
  capability: string;
  provider: string;
  model: string;
  attempt: number;
  status: string;
  error_code: string;
  latency_ms: number;
  input_tokens: number;
  output_tokens: number;
  usage_known: boolean;
};
type AIInvocationPage = {
  items: AIInvocationRecord[];
  total: number;
  page: number;
  page_size: number;
};
type AIQuotaView = {
  enabled: boolean;
  policy: {
    enabled: boolean;
    daily_requests: number;
    daily_tokens: number;
    max_attempts: number;
    max_output_tokens: number;
    max_duration_seconds: number;
  };
  usage: {
    requests: number;
    used_tokens: number;
    reserved_tokens: number;
    unknown_requests: number;
  };
};

const nav = [
  ['/', '运营总览', LayoutDashboard],
  ['/organization-verifications', '主体认证', ShieldCheck],
  ['/providers', '服务商审核', ShieldCheck],
  ['/provider-verifications', '服务商认证', ShieldCheck],
  ['/demands', '需求管理', FileText],
  ['/resources', '资源审核', Camera],
  ['/cases', '案例审核', Image],
  ['/projects', '项目管理', BriefcaseBusiness],
  ['/customer-service', '客服中心', MessageSquare],
  ['/after-sales', '售后与争议', AlertTriangle],
  ['/review-cases', '评价申诉', ShieldCheck],
  ['/orders', '订单管理', Receipt],
  ['/payments', '付款审核', Banknote],
  ['/payment-settings', '支付方式', Settings],
  ['/payment-merchants', '支付商户', BriefcaseBusiness],
  ['/split-transactions', '分账记录', Banknote],
  ['/refunds', '退款记录', Receipt],
  ['/split-reversals', '分账回退', Banknote],
  ['/finance-exceptions', '财务异常', AlertTriangle],
  ['/reconciliation', '支付对账', Activity],
  ['/settlements', '结算记录', Banknote],
  ['/commission', '佣金规则', Settings],
  ['/member-management', '付费会员', Settings],
  ['/digital-resources', '数字资源', Settings],
  ['/ai-models', 'AI 模型', Settings],
  ['/platform-members', '运营人员', Users],
  ['/accounts', '用户管理', Users],
  ['/organizations', '组织管理', Building2],
  ['/cms', '内容管理', FileText],
  ['/audit', '审计日志', Activity],
  ['/notification-email', '邮件投递', Bell],
  ['/retention', '注销与数据留存', ShieldCheck],
  ['/business-timeouts', '业务处理时限', Settings],
] as const;
const navGroups = [
  {
    title: '内容与准入',
    paths: [
      '/organization-verifications',
      '/providers',
      '/provider-verifications',
      '/demands',
      '/resources',
      '/cases',
    ],
  },
  {
    title: '项目与交易',
    paths: ['/projects', '/orders', '/customer-service', '/after-sales', '/review-cases'],
  },
  {
    title: '支付与财务',
    paths: [
      '/payments',
      '/finance-exceptions',
      '/reconciliation',
      '/settlements',
      '/payment-settings',
      '/payment-merchants',
      '/split-transactions',
      '/refunds',
      '/split-reversals',
      '/commission',
      '/member-management',
      '/digital-resources',
    ],
  },
  {
    title: '平台管理',
    paths: [
      '/ai-models',
      '/platform-members',
      '/accounts',
      '/organizations',
      '/cms',
      '/audit',
      '/notification-email',
      '/retention',
      '/business-timeouts',
    ],
  },
] as const;

// Only these paths render the generic Records screen. Dedicated routes (for
// example /notification-email) must not be duplicated here: React Router
// otherwise renders the first matching route and calls the wrong API.
const genericRecordPaths = new Set<string>([
  '/organization-verifications',
  '/providers',
  '/provider-verifications',
  '/demands',
  '/resources',
  '/cases',
  '/projects',
  '/orders',
  '/payments',
  '/payment-merchants',
  '/split-transactions',
  '/refunds',
  '/split-reversals',
  '/reconciliation',
  '/settlements',
  '/cms',
  '/audit',
]);
function canAccess(path: string, role: string) {
  if (role === 'super_admin') return true;
  if (path === '/review-cases')
    return ['operator', 'reviewer', 'customer_service', 'auditor'].includes(role);
  if (role === 'customer_service')
    return ['/', '/customer-service', '/after-sales', '/business-timeouts'].includes(path);
  if (path === '/') return true;
  if (path === '/ai-models') return false;
  if (path === '/platform-members') return false;
  if (path === '/payment-settings') return ['finance', 'auditor'].includes(role);
  if (path === '/digital-resources')
    return ['operator', 'reviewer', 'finance', 'auditor'].includes(role);
  if (path === '/member-management') return ['finance', 'auditor'].includes(role);
  if (
    [
      '/orders',
      '/after-sales',
      '/payments',
      '/payment-merchants',
      '/split-transactions',
      '/refunds',
      '/split-reversals',
      '/finance-exceptions',
      '/reconciliation',
      '/settlements',
      '/commission',
      '/member-management',
    ].includes(path)
  )
    return ['finance', 'auditor'].includes(role);
  if (
    [
      '/organization-verifications',
      '/providers',
      '/provider-verifications',
      '/demands',
      '/resources',
      '/cases',
      '/cms',
    ].includes(path)
  )
    return ['operator', 'reviewer', 'auditor'].includes(role);
  if (path === '/retention') return role === 'auditor';
  if (path === '/business-timeouts')
    return ['operator', 'customer_service', 'finance', 'auditor'].includes(role);
  if (path === '/audit') return role === 'auditor';
  return ['operator', 'auditor'].includes(role);
}
export function App() {
  const location = useLocation();
  if (location.pathname === '/login')
    return (
      <>
        <div className="site-header">
          <div className="container">
            <Brand />
          </div>
        </div>
        <AuthPage admin />
      </>
    );
  return (
    <LoginRequired>
      <Shell />
    </LoginRequired>
  );
}
const hiddenMatchmakingPaths = new Set([
  '/orders',
  '/payments',
  '/payment-merchants',
  '/split-transactions',
  '/refunds',
  '/split-reversals',
  '/finance-exceptions',
  '/reconciliation',
  '/settlements',
  '/commission',
  '/after-sales',
  '/review-cases',
  '/business-timeouts',
]);
function Shell() {
  const session = useSession();
  const [open, setOpen] = useState(false),
    [userMenuOpen, setUserMenuOpen] = useState(false),
    [error, setError] = useState<unknown>(null);
  const userMenu = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const role = session.data?.account.platform_role || '';
  const items = nav.filter(
    ([path]) => canAccess(path, role) && (!collaborationOnly || !hiddenMatchmakingPaths.has(path)),
  );
  const disabledMatchmakingPage =
    collaborationOnly && hiddenMatchmakingPaths.has(useLocation().pathname);
  useEffect(() => {
    if (!userMenuOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!userMenu.current?.contains(event.target as Node)) setUserMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [userMenuOpen]);
  async function signOut() {
    try {
      await logout();
      qc.clear();
      location.assign('/login');
    } catch (e) {
      setError(e);
    }
  }
  return (
    <div className="workspace admin">
      <a href="#main" className="skip-link">
        跳到主要内容
      </a>
      {open && (
        <button aria-label="关闭导航" className="sidebar-overlay" onClick={() => setOpen(false)} />
      )}
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <Brand />
        <span className="sidebar-label">平台运营控制台</span>
        <nav className="sidebar-nav" aria-label="管理后台导航">
          {items
            .filter(([path]) => path === '/')
            .map(([p, t, I]) => (
              <NavLink key={p} to={p} end onClick={() => setOpen(false)}>
                <I size={17} strokeWidth={1.5} />
                {role === 'finance' ? '财务工作台' : t}
              </NavLink>
            ))}
          {navGroups.map((group) => {
            const primary = items.filter(([path]) =>
              (group.paths as readonly string[]).includes(path),
            );
            if (!primary.length) return null;
            return (
              <div className="sidebar-group" key={group.title}>
                <span className="sidebar-label">{group.title}</span>
                {primary.map(([p, t, I]) => (
                  <NavLink key={p} to={p} onClick={() => setOpen(false)}>
                    <I size={17} strokeWidth={1.5} />
                    {t}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-foot">
          <a href={siteURL('www')}>
            访问公开站点
            <ArrowUpRight size={14} />
          </a>
        </div>
      </aside>
      <div className="workspace-body">
        <header className="workspace-header">
          <div className="row">
            <IconButton
              label="打开运营导航"
              className="mobile-toggle"
              onClick={() => setOpen(true)}
            >
              <Menu size={19} />
            </IconButton>
            <span className="small muted">影爸爸 / 平台运营</span>
          </div>
          <div className="workspace-user">
            <span className="tag">
              <ShieldCheck size={12} />
              独立管理会话
            </span>
            <div className="admin-user-menu" ref={userMenu}>
              <button
                type="button"
                className="admin-user-menu-trigger"
                aria-label="打开运营账号菜单"
                aria-expanded={userMenuOpen}
                aria-controls="admin-user-menu-popover"
                onClick={() => setUserMenuOpen((value) => !value)}
              >
                <Avatar
                  name={session.data?.account.name || ''}
                  src={session.data?.account.avatar_url}
                />
                <span className="user-name">{session.data?.account.name}</span>
                <ChevronDown size={15} aria-hidden="true" />
              </button>
              {userMenuOpen && (
                <div
                  id="admin-user-menu-popover"
                  className="admin-user-menu-popover"
                  aria-label="运营账号菜单"
                >
                  <Link
                    to="/account"
                    className="admin-user-menu-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Settings size={16} />
                    账号设置
                  </Link>
                  <button
                    type="button"
                    className="admin-user-menu-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      void signOut();
                    }}
                  >
                    <LogOut size={16} />
                    退出登录
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main id="main" className="workspace-main">
          <ErrorNotice error={error} />
          {disabledMatchmakingPage ? (
            <Empty title="此页面暂不可用" description="请从左侧菜单选择需要管理的内容。" />
          ) : (
            <Routes>
              <Route
                path="/"
                element={
                  role === 'finance' && collaborationOnly ? (
                    <CollectionWorkbench />
                  ) : role === 'finance' ? (
                    <FinanceWorkbench />
                  ) : role === 'customer_service' ? (
                    <CustomerService />
                  ) : (
                    <Overview />
                  )
                }
              />
              {items
                .filter(([path]) => genericRecordPaths.has(path))
                .map(([path, label]) => (
                  <Route
                    key={path}
                    path={path}
                    element={<Records key={path} kind={path.slice(1)} title={label} />}
                  />
                ))}
              <Route path="/account" element={<AdminAccount />} />
              <Route path="/ai-models" element={<AIModels />} />
              <Route
                path="/payment-settings"
                element={
                  canAccess('/payment-settings', role) ? (
                    <PaymentSettings />
                  ) : (
                    <Notice>当前运营角色无权查看支付方式。</Notice>
                  )
                }
              />
              <Route path="/platform-members" element={<PlatformMembers />} />
              <Route
                path="/retention"
                element={
                  canAccess('/retention', role) ? (
                    <LifecycleManagement />
                  ) : (
                    <Empty title="当前角色无权限" />
                  )
                }
              />
              <Route
                path="/business-timeouts"
                element={
                  canAccess('/business-timeouts', role) ? (
                    <BusinessTimeoutSettings />
                  ) : (
                    <Empty title="当前角色无权限" />
                  )
                }
              />
              <Route path="/commission" element={<CommissionSettings />} />
              <Route
                path="/digital-resources"
                element={
                  canAccess('/digital-resources', role) ? (
                    <DigitalManagement />
                  ) : (
                    <Empty title="当前角色不能访问数字资源管理" />
                  )
                }
              />
              <Route
                path="/member-management"
                element={
                  canAccess('/member-management', role) ? (
                    <MemberManagement />
                  ) : (
                    <Empty title="当前角色无会员管理权限" />
                  )
                }
              />
              <Route path="/finance-exceptions" element={<FinanceWorkbench />} />
              <Route path="/notification-email" element={<BusinessEmailDeliveries />} />
              <Route path="/customer-service" element={<CustomerService />} />
              <Route path="/after-sales" element={<AfterSalesWorkbench />} />
              <Route path="/review-cases" element={<ReviewModeration />} />
              <Route path="/support-cases/:caseID" element={<CustomerService />} />
              <Route
                path="/accounts"
                element={
                  canAccess('/accounts', role) ? (
                    <AccountsDirectory />
                  ) : (
                    <Empty title="当前角色没有用户管理权限" />
                  )
                }
              />
              <Route
                path="/organizations"
                element={
                  canAccess('/organizations', role) ? (
                    <OrganizationsDirectory />
                  ) : (
                    <Empty title="当前角色没有组织管理权限" />
                  )
                }
              />
              <Route
                path="/organizations/:organizationID"
                element={
                  canAccess('/organizations', role) ? (
                    <OrganizationDetail />
                  ) : (
                    <Empty title="当前角色没有组织管理权限" />
                  )
                }
              />
              <Route path="*" element={<Empty title="页面不存在" />} />
            </Routes>
          )}
        </main>
      </div>
    </div>
  );
}
function AIProbeButton({
  path,
  label,
  billable = false,
}: {
  path: string;
  label: string;
  billable?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<AIDiagnosticResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const qc = useQueryClient();
  async function run() {
    setBusy(true);
    setError(null);
    try {
      const data = billable
        ? await postIdempotent<AIDiagnosticResult>(path, {}, crypto.randomUUID())
        : await post<AIDiagnosticResult>(path, {});
      setResult(data);
      await qc.invalidateQueries();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack compact">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => (billable ? setConfirmOpen(true) : run())}
        disabled={busy}
      >
        {busy ? '测试中…' : billable ? '真实调用 · 可能产生费用' : label}
      </Button>
      <Modal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="确认真实模型测试"
        description="本次测试会向模型服务商发送请求，可能产生实际调用费用，并计入调用记录。"
      >
        <div className="form-actions">
          <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
            取消
          </Button>
          <Button
            loading={busy}
            onClick={() => {
              setConfirmOpen(false);
              void run();
            }}
          >
            确认调用模型
          </Button>
        </div>
      </Modal>
      {result ? (
        <span className="small muted">
          {result.ok ? '通过' : result.error_code || '失败'} · {result.latency_ms}ms
          {result.billable ? ' · 已记录一次真实模型调用' : ' · 不产生模型调用费用'}
        </span>
      ) : null}
      {error ? <ErrorNotice error={error} /> : null}
    </div>
  );
}

function aiInvocationStatusLabel(status: string) {
  return (
    {
      queued: '排队中',
      running: '调用中',
      succeeded: '调用成功',
      failed: '调用失败',
      cancelled: '已取消',
    } as Record<string, string>
  )[status];
}

function AIHealthLine({ health }: { health: AIHealthSummary }) {
  return (
    <span className="small muted">
      {health.window_hours}h 调用 {health.attempts} · 成功 {health.successes} · 失败{' '}
      {health.failures}
      {health.timeouts ? ` · 超时 ${health.timeouts}` : ''}
      {health.attempts ? ` · 平均 ${health.avg_latency_ms}ms` : ''}
      {health.last_invoked_at
        ? ` · 最近 ${aiInvocationStatusLabel(health.last_status) || health.last_status}${health.last_error_code ? `/${health.last_error_code}` : ''}`
        : ' · 暂无调用记录'}
    </span>
  );
}

function AIModels() {
  const q = useData<AIProviderConfig[]>('/admin/ai/providers');
  const invocations = useData<AIInvocationPage>('/admin/ai/invocations?page=1&page_size=20');
  const quota = useData<AIQuotaView>('/admin/ai/quota?scope=global');
  const enabledModels =
    q.data?.reduce(
      (total, provider) =>
        total + (provider.enabled ? provider.models.filter((model) => model.enabled).length : 0),
      0,
    ) || 0;
  const providerFields = (provider?: AIProviderConfig) => [
    { name: 'name', label: 'Provider 名称', value: provider?.name || '' },
    {
      name: 'type',
      label: 'Provider 类型',
      options: [{ value: 'openai_compatible', label: 'OpenAI Compatible' }],
      value: provider?.type || 'openai_compatible',
    },
    {
      name: 'base_url',
      label: 'Base URL',
      value: provider?.base_url || '',
      placeholder: 'https://api.example.com/v1',
    },
    {
      name: 'api_key',
      label: provider?.api_key_configured ? 'API Key（留空保持不变）' : 'API Key（可选）',
      type: 'password',
      required: false,
    },
    {
      name: 'priority',
      label: 'Provider 优先级',
      type: 'number',
      min: 0,
      max: 100000,
      value: String(provider?.priority ?? 100),
      hint: '数值越小优先级越高；模型优先级相同时使用 Provider 优先级。',
    },
    {
      name: 'timeout_seconds',
      label: '单次调用超时（秒）',
      type: 'number',
      min: 1,
      max: 300,
      value: String(provider?.timeout_seconds ?? 90),
    },
    {
      name: 'enabled',
      label: '状态',
      options: [
        { value: 'true', label: '启用' },
        { value: 'false', label: '停用' },
      ],
      value: String(provider?.enabled ?? true),
    },
  ];
  const modelFields = (model?: AIModelConfig) => [
    { name: 'name', label: '模型名称', value: model?.name || '' },
    { name: 'model', label: '请求 Model ID', value: model?.model || '', placeholder: 'gpt-5.6' },
    {
      name: 'capabilities',
      label: '能力',
      options: [{ value: 'text_generation', label: '文本生成' }],
      value: model?.capabilities || 'text_generation',
      hint: '当前仅已实现 text_generation；其他能力将在对应 Adapter 完成后开放。',
    },
    {
      name: 'priority',
      label: '模型优先级',
      type: 'number',
      min: 0,
      max: 100000,
      value: String(model?.priority ?? 100),
      hint: '数值越小越先尝试；调用失败时自动继续尝试下一个符合能力要求的模型。',
    },
    {
      name: 'context_window',
      label: 'Context Window',
      type: 'number',
      min: 1,
      value: String(model?.context_window ?? 8192),
    },
    {
      name: 'max_output_tokens',
      label: '最大输出 Token',
      type: 'number',
      min: 1,
      value: String(model?.max_output_tokens ?? 4096),
    },
    {
      name: 'enabled',
      label: '状态',
      options: [
        { value: 'true', label: '启用' },
        { value: 'false', label: '停用' },
      ],
      value: String(model?.enabled ?? true),
    },
  ];
  const providerTransform = (v: Record<string, string>) => ({
    ...v,
    enabled: v.enabled === 'true',
    priority: Number(v.priority),
    timeout_seconds: Number(v.timeout_seconds),
  });
  const modelTransform = (v: Record<string, string>) => ({
    ...v,
    enabled: v.enabled === 'true',
    priority: Number(v.priority),
    context_window: Number(v.context_window),
    max_output_tokens: Number(v.max_output_tokens),
  });
  const quotaFields = [
    {
      name: 'enabled',
      label: 'AI 总开关',
      options: [
        { value: 'true', label: '启用' },
        { value: 'false', label: '暂停' },
      ],
      value: String(quota.data?.policy.enabled ?? true),
    },
    {
      name: 'daily_requests',
      label: '每日上游调用次数',
      type: 'number',
      min: 1,
      value: String(quota.data?.policy.daily_requests ?? 1000),
    },
    {
      name: 'daily_tokens',
      label: '每日 Token 预算',
      type: 'number',
      min: 1,
      value: String(quota.data?.policy.daily_tokens ?? 20000000),
    },
    {
      name: 'max_attempts',
      label: '单任务最多尝试模型数',
      type: 'number',
      min: 1,
      max: 20,
      value: String(quota.data?.policy.max_attempts ?? 8),
    },
    {
      name: 'max_output_tokens',
      label: '单次最大输出 Token',
      type: 'number',
      min: 1,
      max: 16000,
      value: String(quota.data?.policy.max_output_tokens ?? 4096),
    },
    {
      name: 'max_duration_seconds',
      label: '单任务总时长上限（秒）',
      type: 'number',
      min: 1,
      max: 3600,
      value: String(quota.data?.policy.max_duration_seconds ?? 900),
    },
  ];
  const quotaTransform = (v: Record<string, string>) => ({
    enabled: v.enabled === 'true',
    daily_requests: Number(v.daily_requests),
    daily_tokens: Number(v.daily_tokens),
    max_attempts: Number(v.max_attempts),
    max_output_tokens: Number(v.max_output_tokens),
    max_duration_seconds: Number(v.max_duration_seconds),
  });
  return (
    <>
      <PageTitle
        title="AI 模型"
        description="统一配置多个 Provider 和多个 Model；业务按能力与优先级选择模型，调用失败后自动切换到下一候选。"
        action={
          <ActionDialog
            title="新增 AI Provider"
            trigger={
              <>
                <Plus size={15} /> 新增 Provider
              </>
            }
            path="/admin/ai/providers"
            fields={providerFields()}
            transform={providerTransform}
          />
        }
      />
      <div className="stats">
        <Stat
          label="Provider"
          value={q.data?.length ?? '—'}
          foot="数据库配置"
          icon={<Settings size={18} />}
        />
        <Stat
          label="启用模型"
          value={enabledModels}
          foot="可参与能力路由"
          icon={<Activity size={18} />}
        />
        <Stat
          label="今日 AI 调用"
          value={quota.data?.usage.requests ?? '—'}
          foot={
            quota.data
              ? `${quota.data.usage.used_tokens.toLocaleString()} tokens 已计入`
              : '全局额度'
          }
          icon={<Activity size={18} />}
        />
      </div>
      <QueryState query={q} empty={!q.data?.length}>
        <div className="stack">
          {q.data?.map((provider) => (
            <section className="panel stack" key={provider.id}>
              <div className="panel-title">
                <div>
                  <div className="row wrap">
                    <h3>{provider.name}</h3>
                    <Status
                      value={provider.enabled ? 'active' : 'disabled'}
                      label={provider.enabled ? '已启用' : '已停用'}
                    />
                  </div>
                  <p className="small muted">
                    {provider.type} · 优先级 {provider.priority} · 超时 {provider.timeout_seconds}s
                    · API Key {provider.api_key_configured ? '已配置' : '未配置'}
                  </p>
                  <p className="small muted">{provider.base_url}</p>
                  <p>
                    <AIHealthLine health={provider.health} />
                  </p>
                </div>
                <div className="row wrap">
                  <AIProbeButton
                    path={`/admin/ai/providers/${provider.id}/test`}
                    label="测试连接"
                  />
                  <ActionDialog
                    title="编辑 AI Provider"
                    trigger="编辑 Provider"
                    path={`/admin/ai/providers/${provider.id}`}
                    method="PUT"
                    fields={providerFields(provider)}
                    transform={providerTransform}
                  />
                  <ActionDialog
                    title={`为 ${provider.name} 新增模型`}
                    trigger={
                      <>
                        <Plus size={15} /> 新增 Model
                      </>
                    }
                    path={`/admin/ai/providers/${provider.id}/models`}
                    fields={modelFields()}
                    transform={modelTransform}
                  />
                </div>
              </div>
              {provider.models.length ? (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>模型</th>
                        <th>能力</th>
                        <th>优先级</th>
                        <th>上下文 / 输出</th>
                        <th>状态</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {provider.models.map((model) => (
                        <tr key={model.id}>
                          <td>
                            <strong>{model.name}</strong>
                            <span className="sub-cell">{model.model}</span>
                          </td>
                          <td>{model.capabilities.split(',').join(' / ')}</td>
                          <td>{model.priority}</td>
                          <td>
                            {model.context_window.toLocaleString()} /{' '}
                            {model.max_output_tokens.toLocaleString()}
                          </td>
                          <td>
                            <Status
                              value={model.enabled ? 'active' : 'disabled'}
                              label={model.enabled ? '已启用' : '已停用'}
                            />
                            <span className="sub-cell">
                              <AIHealthLine health={model.health} />
                            </span>
                          </td>
                          <td>
                            <div className="stack compact">
                              <AIProbeButton
                                path={`/admin/ai/providers/${provider.id}/models/${model.id}/test`}
                                label="测试模型"
                                billable
                              />
                              <ActionDialog
                                title="编辑 AI Model"
                                trigger="编辑"
                                path={`/admin/ai/providers/${provider.id}/models/${model.id}`}
                                method="PUT"
                                fields={modelFields(model)}
                                transform={modelTransform}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Empty
                  title="这个 Provider 还没有配置模型"
                  description="至少添加一个启用且声明能力的 Model 才会参与 AI 路由。"
                />
              )}
            </section>
          ))}
        </div>
      </QueryState>
      <section className="panel stack">
        <div className="panel-title">
          <div>
            <h3>AI 全局调用额度</h3>
            <p className="small muted">
              fallback 和模型测试都按真实上游尝试计入调用次数；未知 usage
              会按预留额度计入，不会被当成免费调用。
            </p>
          </div>
          <ActionDialog
            title="编辑 AI 全局额度"
            trigger="编辑额度"
            path="/admin/ai/quota?scope=global"
            method="PUT"
            fields={quotaFields}
            transform={quotaTransform}
          />
        </div>
        {quota.data ? (
          <p className="small muted">
            今日 {quota.data.usage.requests.toLocaleString()} /{' '}
            {quota.data.policy.daily_requests.toLocaleString()} 次 · Token{' '}
            {quota.data.usage.used_tokens.toLocaleString()} + 预留{' '}
            {quota.data.usage.reserved_tokens.toLocaleString()} /{' '}
            {quota.data.policy.daily_tokens.toLocaleString()}
            {quota.data.usage.unknown_requests
              ? ` · ${quota.data.usage.unknown_requests} 次 usage 未知`
              : ''}
          </p>
        ) : null}
      </section>
      <section className="panel stack">
        <div className="panel-title">
          <div>
            <h3>最近 AI 调用</h3>
            <p className="small muted">
              最近 20 次真实上游调用，包含 fallback
              与模型测试。错误只展示标准化错误码，不回显上游响应正文或密钥。
            </p>
          </div>
          <span className="small muted">共 {invocations.data?.total ?? '—'} 条</span>
        </div>
        <QueryState query={invocations} empty={!invocations.data?.items.length}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>时间</th>
                  <th>Provider / Model</th>
                  <th>用途</th>
                  <th>状态</th>
                  <th>耗时</th>
                  <th>Token</th>
                </tr>
              </thead>
              <tbody>
                {invocations.data?.items.map((row) => (
                  <tr key={row.id}>
                    <td>{datetime(row.created_at)}</td>
                    <td>
                      <strong>{row.provider}</strong>
                      <span className="sub-cell">{row.model}</span>
                    </td>
                    <td>
                      {row.purpose}
                      <span className="sub-cell">
                        {row.capability} · attempt {row.attempt}
                      </span>
                    </td>
                    <td>
                      <Status value={row.status} label={aiInvocationStatusLabel(row.status)} />
                      {row.error_code ? <span className="sub-cell">{row.error_code}</span> : null}
                    </td>
                    <td>{row.latency_ms.toLocaleString()} ms</td>
                    <td>
                      {row.usage_known
                        ? `${row.input_tokens.toLocaleString()} + ${row.output_tokens.toLocaleString()}`
                        : '未知/按预留计入'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </section>
    </>
  );
}

function Overview() {
  const session = useSession();
  const canMarket = canAccess('/demands', session.data?.account.platform_role || '');
  const q = useData<Record<string, number>>('/admin/overview');
  const pending = useList<Row>('/admin/demands?status=pending_review&page_size=5', canMarket);
  const reviews = useList<Row>(
    '/admin/review-cases?status=active&page_size=1',
    canAccess('/review-cases', session.data?.account.platform_role || ''),
  );
  return (
    <>
      <PageTitle title="运营总览" description="关注真实进展，及时处理每一份期待。" />
      {!!reviews.data?.total && (
        <Notice>
          有 {reviews.data.total} 条评价申诉或复核等待处理。
          <Link to="/review-cases" className="link">
            进入处理队列
          </Link>
        </Notice>
      )}
      <QueryState query={q}>
        <div className="stats">
          {[
            ['平台需求', 'demands', FileText],
            ['已建项目', 'projects', BriefcaseBusiness],
            ['审核通过团队', 'providers', ShieldCheck],
            ['待审核需求', 'pending_demands', Activity],
          ].map(([t, k, I]) => {
            const Icon = I as typeof FileText;
            return (
              <Stat
                key={String(k)}
                label={String(t)}
                value={q.data?.[String(k)] ?? '—'}
                foot="平台数据库实时汇总"
                icon={<Icon size={18} />}
              />
            );
          })}
        </div>
      </QueryState>
      {canMarket ? (
        <div className="work-grid">
          <section className="panel">
            <div className="panel-title">
              <h3>待审核需求</h3>
              <Link to="/demands" className="link small">
                全部需求
                <ArrowRight size={14} />
              </Link>
            </div>
            <QueryState query={pending}>
              {pending.data?.items.length ? (
                pending.data.items.map((d) => (
                  <div className="list-item" key={d.id}>
                    <span className="icon-tile">
                      <FileText size={20} />
                    </span>
                    <div className="grow">
                      <strong>{d.title}</strong>
                      <p>
                        {d.category} · {d.city}
                      </p>
                    </div>
                    <ReviewActions kind="demands" row={d} />
                  </div>
                ))
              ) : (
                <Empty title="当前没有待审核需求" description="新需求提交后会出现在这里。" />
              )}
            </QueryState>
          </section>
          <aside className="stack">
            <section className="panel">
              <h3>待办事项</h3>
              {[
                ['服务商入驻', 'pending_providers', '/providers'],
                ['资源上架', 'pending_resources', '/resources'],
                ['拍片需求', 'pending_demands', '/demands'],
              ].map(([title, key, path]) => (
                <Link to={path} className="list-item" key={key}>
                  <div className="grow">{title}</div>
                  <strong className="text-accent">{q.data?.[key] || 0}</strong>
                  <ArrowUpRight size={16} />
                </Link>
              ))}
            </section>
            <Notice>
              审核、付款确认、佣金调整与结算操作都记录到审计日志。财务变更需要填写具体理由。
            </Notice>
          </aside>
        </div>
      ) : (
        <section className="panel stack">
          <h3>财务工作台</h3>
          <p className="muted">核实付款凭证、查看应结金额，并记录实际完成的结算。</p>
          <div className="row wrap">
            <Link className="btn btn-primary" to="/payments">
              处理付款记录
            </Link>
            <Link className="btn btn-secondary" to="/settlements">
              查看结算记录
            </Link>
          </div>
        </section>
      )}
    </>
  );
}
function ReviewActions({ kind, row }: { kind: string; row: Row }) {
  const session = useSession();
  const canReview = ['super_admin', 'reviewer', 'operator'].includes(
    session.data?.account.platform_role || '',
  );
  if (!canReview) return <Status value={row.status || ''} />;
  if (kind === 'organization-verifications' && row.status === 'verified') {
    return (
      <div className="table-actions">
        <Status value={row.status || ''} />
        <ActionDialog
          title="撤销主体认证"
          trigger="撤销认证"
          variant="secondary"
          path={`/admin/${kind}/${row.id}/review`}
          description="撤销后，该主体会立即失去新的公开发布、接单交易和收款准入资格；已有履约项目不被中断。"
          fields={[{ name: 'reason', label: '撤销原因', type: 'textarea', min: 2 }]}
          transform={(v) => ({ ...v, action: 'revoke' })}
        />
      </div>
    );
  }
  if (row.status !== 'pending_review') {
    const approved =
      (kind === 'providers' && row.status === 'verified') ||
      (kind === 'cases' && row.status === 'published');
    if (!approved) return <Status value={row.status || ''} />;
    return (
      <div className="table-actions">
        <Status value={row.status || ''} />
        <ActionDialog
          title="设为精选"
          trigger="设为精选"
          path={`/admin/marketplace-feature/${kind}/${row.id}`}
          fields={[{ name: 'position', label: '排序值（数字越小越靠前）', type: 'number' }]}
          transform={(v) => ({ featured: true, position: Number(v.position || 0) })}
        />
        <ActionDialog
          title="取消精选"
          trigger="取消精选"
          variant="secondary"
          path={`/admin/marketplace-feature/${kind}/${row.id}`}
          fields={[]}
          transform={() => ({ featured: false, position: 0 })}
        />
      </div>
    );
  }
  const commissionProfileField =
    kind === 'providers' || kind === 'resources'
      ? [
          {
            name: 'commission_profile',
            label: '费率身份',
            options:
              kind === 'resources'
                ? [
                    { value: '', label: '请选择并确认费率身份' },
                    { value: 'material_creator', label: '素材创作者' },
                    { value: 'supply_chain', label: '供应链公司' },
                  ]
                : [
                    { value: '', label: '请选择并确认费率身份' },
                    { value: 'film_company', label: '影视公司（企业）' },
                    { value: 'supply_chain', label: '供应链公司（企业）' },
                    { value: 'individual_creator', label: '个人创作者（个人）' },
                  ],
          },
        ]
      : [];
  return (
    <div className="table-actions">
      <ActionDialog
        title="确认审核通过"
        trigger="通过"
        path={`/admin/${kind}/${row.id}/review`}
        description={
          commissionProfileField.length
            ? '费率身份是结算依据，必须由平台审核人员确认，不能由服务商提交的分类自动决定。'
            : undefined
        }
        fields={[
          ...commissionProfileField,
          { name: 'reason', label: '审核结论与理由', type: 'textarea', min: 2 },
        ]}
        transform={(v) => ({ ...v, action: 'approve' })}
      />
      <ActionDialog
        title="退回审核申请"
        trigger="退回"
        variant="secondary"
        path={`/admin/${kind}/${row.id}/review`}
        fields={[{ name: 'reason', label: '请说明需要补充或修正的内容', type: 'textarea', min: 2 }]}
        transform={(v) => ({ ...v, action: 'reject' })}
      />
    </div>
  );
}

function VerificationMaterials({ row }: { row: Row }) {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<OrganizationVerificationAdminDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<unknown>(null);
  const role = session.data?.account.platform_role || '';
  const canReadSensitive = ['super_admin', 'operator', 'reviewer'].includes(role);
  useEffect(() => {
    let cancelled = false;
    if (!open || !canReadSensitive) {
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    void api<OrganizationVerificationAdminDetail>(
      `/admin/organization-verifications/${row.id}/detail`,
    )
      .then((value) => {
        if (!cancelled) setDetail(value);
      })
      .catch((error) => {
        if (!cancelled) setDetailError(error);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
      setDetail(null);
    };
  }, [canReadSensitive, open, row.id]);
  const labels: Record<string, string> = {
    id_front: '身份证人像面',
    id_back: '身份证国徽面',
    business_license: '营业执照',
    legal_representative_id_front: '法定代表人身份证人像面',
    legal_representative_id_back: '法定代表人身份证国徽面',
  };
  if (!canReadSensitive) return <span className="small muted">敏感材料仅审核角色可见</span>;
  const application = detail?.application;
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        查看审核材料
      </Button>
      <Modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setDetail(null);
            setDetailError(null);
          }
        }}
        title="主体认证审核材料"
        description="此页面包含身份证号码和证件原件。打开材料预览、查看详情或下载原件都会写入审计日志。"
      >
        {detailLoading ? (
          <p className="muted">正在读取加密认证材料…</p>
        ) : detailError != null ? (
          <ErrorNotice error={detailError} />
        ) : detail ? (
          <div className="stack">
            <section className="stack small">
              <p>
                <strong>主体：</strong>
                {application?.subject_type === 'enterprise' ? '企业' : '个人'}
              </p>
              <p>
                <strong>法定 / 实名名称：</strong>
                {application?.legal_name || '—'}
              </p>
              {application?.subject_type === 'individual' ? (
                <p>
                  <strong>居民身份证号：</strong>
                  <code>{detail.identity_number || '—'}</code>
                </p>
              ) : (
                <>
                  <p>
                    <strong>统一社会信用代码：</strong>
                    <code>{detail.unified_social_credit_code || '—'}</code>
                  </p>
                  <p>
                    <strong>法定代表人：</strong>
                    {application?.legal_representative_name || '—'}
                  </p>
                  <p>
                    <strong>法定代表人身份证号：</strong>
                    <code>{detail.legal_representative_id_number || '—'}</code>
                  </p>
                </>
              )}
              {application?.reference && (
                <p>
                  <strong>补充说明：</strong>
                  {application.reference}
                </p>
              )}
            </section>
            <section className="stack">
              <h3>证件原件</h3>
              {detail.documents.length ? (
                <div className="stack">
                  {detail.documents.map((document) => (
                    <div key={document.id} className="stack" style={{ gap: 8 }}>
                      <PrivateDocumentPreview
                        path={`/admin/organization-verifications/${row.id}/documents/${document.id}/download`}
                        fileName={document.file_name}
                        mimeType={document.mime}
                        size={document.size}
                        label={labels[document.kind] || document.kind}
                      />
                      <span className="muted small">SHA-256 {document.sha256.slice(0, 12)}…</span>
                    </div>
                  ))}
                </div>
              ) : (
                <Notice>没有找到已提交的证件材料。</Notice>
              )}
            </section>
          </div>
        ) : null}
      </Modal>
    </>
  );
}

function Records({ kind, title }: { kind: string; title: string }) {
  const [params, setParams] = useSearchParams();
  const filter = params.get('status') || '',
    search = params.get('q') || '';
  const requestedPage = Number(params.get('page'));
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const organizationID = params.get('organization_id') || '';
  function change(values: Record<string, string>, resetPage = true) {
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (resetPage) next.delete('page');
      for (const [key, value] of Object.entries(values)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    });
  }
  const market = ['providers', 'demands', 'resources', 'cases'].includes(kind);
  const scoped =
    market ||
    ['projects', 'orders', 'organization-verifications', 'provider-verifications'].includes(kind);
  const q = useList<Row>(
    `/admin/${kind}?page=${page}&page_size=15${market ? '&status=' + encodeURIComponent(filter) + '&q=' + encodeURIComponent(search) : ''}${scoped && organizationID ? '&organization_id=' + encodeURIComponent(organizationID) : ''}`,
  );
  const session = useSession();
  const fin = ['super_admin', 'finance'].includes(session.data?.account.platform_role || '');
  const operator = ['super_admin', 'operator'].includes(session.data?.account.platform_role || '');
  return (
    <>
      <PageTitle
        title={title}
        description={
          {
            providers: '查看团队资料与作品，审核每一位创作伙伴。',
            'organization-verifications':
              '审核个人与企业主体认证。主体认证是公开发布、接单交易和收款准入的基础，不替代后续服务商/资源业务审核。',
            'provider-verifications':
              '核验经营资质和作品真实性申请；主体身份由独立的个人/企业认证流程负责。',
            demands: '审核需求信息，保持清晰、可靠的项目市场。',
            payments: '核实真实付款凭证后确认，确认操作启动项目履约。',
            settlements: '根据已验收订单的应结金额，记录实际完成的结算。',
            audit: '不可由运营页面删除的关键操作记录。',
          }[kind] || '查看平台记录，按业务流程处理相关事项。'
        }
        action={
          kind === 'cms' && operator ? (
            <ActionDialog
              title="编辑内容模块"
              trigger={
                <>
                  <Plus size={15} />
                  内容模块
                </>
              }
              path="/admin/cms"
              description="首页公告使用 home.announcement 标识；同一标识可再次编辑或停用。"
              fields={[
                { name: 'key', label: '模块标识（已有标识将更新）' },
                { name: 'title', label: '标题' },
                { name: 'content', label: '正文', type: 'textarea' },
                { name: 'link', label: '站内链接', required: false },
                {
                  name: 'enabled',
                  label: '显示状态',
                  options: [
                    { value: 'true', label: '启用' },
                    { value: 'false', label: '停用' },
                  ],
                },
              ]}
              transform={(v) => ({ ...v, enabled: v.enabled === 'true' })}
            />
          ) : kind === 'payment-merchants' &&
            session.data?.account.platform_role === 'super_admin' ? (
            <PaymentMerchantDialog />
          ) : null
        }
      />
      {scoped && organizationID && (
        <Notice>
          当前列表已限定组织。
          <Link className="directory-link" to={`/organizations/${organizationID}`}>
            查看组织详情
          </Link>
          {' · '}
          <Link className="directory-link" to={`/${kind}`}>
            查看全部记录
          </Link>
        </Notice>
      )}
      {market && (
        <div className="filter-bar">
          <Select
            aria-label="审核状态"
            value={filter}
            onChange={(e) => {
              change({ status: e });
            }}
          >
            <option value="">全部状态</option>
            <option value="pending_review">待审核</option>
            <option value={kind === 'providers' ? 'verified' : 'published'}>已通过</option>
            <option value="rejected">未通过</option>
          </Select>
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              change({ q: String(new FormData(e.currentTarget).get('q') || '').trim() });
            }}
          >
            <input
              className="input"
              aria-label="搜索记录"
              name="q"
              key={search}
              defaultValue={search}
              placeholder="搜索名称或标题"
            />
            <Button variant="secondary">搜索</Button>
          </form>
          <span className="result-count">共 {q.data?.total ?? '…'} 条</span>
        </div>
      )}
      <QueryState query={q} empty={!q.data?.items.length}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {headers(kind).map((t) => (
                  <th key={t}>{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.data?.items.map((row) => (
                <RecordRow key={row.id} kind={kind} row={row} finance={fin} />
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
      <Pagination
        page={page}
        total={q.data?.total || 0}
        size={15}
        onPage={(n) => change({ page: String(n) }, false)}
      />
    </>
  );
}
function headers(k: string) {
  if (['providers', 'demands', 'resources', 'cases'].includes(k))
    return ['内容', '分类 / 城市', '介绍与说明', '状态', '提交时间', '审核操作'];
  if (k === 'provider-verifications')
    return ['认证类型', '组织', '核验说明 / 凭证', '状态', '审核意见', '操作'];
  if (k === 'organization-verifications')
    return ['主体类型', '法定 / 实名名称', '审核材料', '状态', '审核意见', '操作'];
  if (k === 'audit') return ['操作', '对象', '操作人', '变更', '理由', '时间'];
  if (k === 'orders') return ['订单', '总额', '佣金', '服务商应收', '状态', '创建时间'];
  if (k === 'payment-merchants')
    return ['服务商组织', '支付渠道', '渠道商户号', '入驻状态', '能力', '更新时间'];
  if (k === 'split-transactions')
    return ['分账单', '渠道 / 二级商户', '平台佣金', '服务商金额', '状态', '时间', '操作'];
  if (k === 'refunds') return ['退款单', '金额', '平台 / 服务商回退', '状态', '原因', '操作'];
  if (k === 'split-reversals')
    return ['回退单', '原分账', '平台 / 服务商回退', '状态', '时间', '操作'];
  if (k === 'reconciliation')
    return ['渠道记录', '账单日期', '平台 / 渠道金额', '手续费 / 差额', '状态', '操作'];
  if (k === 'payments' || k === 'settlements')
    return ['记录', '金额', '凭证', '状态', '时间', '操作'];
  if (k === 'cms') return ['标识', '标题', '正文', '链接', '状态'];
  return ['项目', '类型', '状态', '计划交付', '创建时间'];
}
function adminRecordStatusLabel(kind: string, status: string) {
  const maps: Record<string, Record<string, string>> = {
    'payment-merchants': {
      pending: '申请中',
      active: '已启用',
      disabled: '已停用',
      rejected: '未通过',
    },
    'split-transactions': {
      ready: '待提交',
      pending: '待查单',
      succeeded: '已分账',
      failed: '分账失败',
      cancelled: '已取消',
    },
    refunds: {
      manual_review: '待人工退款',
      ready: '待退款',
      pending: '退款处理中',
      succeeded: '已退款',
      failed: '退款失败',
      cancelled: '已取消',
    },
    'split-reversals': {
      ready: '待回退',
      pending: '回退处理中',
      succeeded: '已回退',
      failed: '回退失败',
      cancelled: '已取消',
    },
    reconciliation: {
      matched: '已匹配',
      resolved: '已处理',
      amount_mismatch: '金额不一致',
      currency_mismatch: '币种不一致',
      reference_mismatch: '单号不一致',
      status_mismatch: '状态不一致',
      missing_local: '本地缺失',
      provider_query_failed: '渠道查询失败',
    },
  };
  return maps[kind]?.[status];
}

function RecordRow({ kind: k, row: r, finance }: { kind: string; row: Row; finance: boolean }) {
  if (['providers', 'demands', 'resources', 'cases'].includes(k))
    return (
      <tr>
        <td>
          <span className="title-cell">{r.name || r.title}</span>
          <span className="sub-cell">
            {r.id.slice(-8)}
            {r.is_sample ? ' · 示例内容' : ''}
          </span>
        </td>
        <td>
          {r.category}
          <span className="sub-cell">{r.city}</span>
        </td>
        <td style={{ minWidth: 180, maxWidth: 300 }}>
          {k === 'cases' && <PortfolioReview id={r.id} />}
          <details>
            <summary className="link">展开完整内容</summary>
            <p className="prose" style={{ marginTop: 10 }}>
              {r.description || r.summary}
            </p>
            {r.review_reason && <p className="small muted">审核：{r.review_reason}</p>}
          </details>
        </td>
        <td>
          <Status value={r.status || ''} />
        </td>
        <td className="small muted">{datetime(r.created_at)}</td>
        <td>
          <ReviewActions kind={k} row={r} />
        </td>
      </tr>
    );
  if (k === 'provider-verifications')
    return (
      <tr>
        <td>
          <strong>
            {r.type === 'business'
              ? '企业主体'
              : r.type === 'portfolio'
                ? '作品真实性'
                : String(r.type || '')}
          </strong>
        </td>
        <td className="small">{r.organization_id?.slice(-8)}</td>
        <td style={{ maxWidth: 320 }}>{String(r.reference || '—')}</td>
        <td>
          <Status value={r.status || ''} />
        </td>
        <td>{r.review_reason || '—'}</td>
        <td>
          <ReviewActions kind="provider-verifications" row={r} />
        </td>
      </tr>
    );
  if (k === 'organization-verifications')
    return (
      <tr>
        <td>
          <strong>
            {r.subject_type === 'individual'
              ? '个人'
              : r.subject_type === 'enterprise'
                ? '企业'
                : String(r.subject_type || '—')}
          </strong>
          <span className="sub-cell">组织 {r.organization_id?.slice(-8)}</span>
        </td>
        <td>{String(r.legal_name || '—')}</td>
        <td style={{ maxWidth: 320 }}>
          <VerificationMaterials row={r} />
        </td>
        <td>
          <Status value={r.status || ''} />
        </td>
        <td>{r.review_reason || '—'}</td>
        <td>
          <ReviewActions kind="organization-verifications" row={r} />
        </td>
      </tr>
    );
  if (k === 'audit')
    return (
      <tr>
        <td>
          <strong>{r.action}</strong>
        </td>
        <td className="small">{r.entity_id?.slice(-8)}</td>
        <td className="small">{r.account_id?.slice(-8)}</td>
        <td className="small">
          {r.before || r.after ? `${r.before || '—'} → ${r.after || '—'}` : '—'}
        </td>
        <td style={{ maxWidth: 300 }}>{r.reason || '—'}</td>
        <td>{datetime(r.created_at)}</td>
      </tr>
    );
  if (k === 'orders')
    return (
      <tr>
        <td>{r.id.slice(-8)}</td>
        <td>{money(r.gross_amount_minor || 0)}</td>
        <td>{money(r.commission_amount_minor || 0)}</td>
        <td>{money(r.provider_receivable_minor || 0)}</td>
        <td>
          <Status value={r.status || ''} />
        </td>
        <td>{datetime(r.created_at)}</td>
      </tr>
    );
  if (k === 'payment-merchants')
    return (
      <tr>
        <td className="small">{r.organization_id?.slice(-8)}</td>
        <td>
          <strong>{r.provider}</strong>
          <span className="sub-cell">{r.onboarding_reference || '—'}</span>
        </td>
        <td>{r.external_merchant_id || '—'}</td>
        <td>
          <Status value={r.status || ''} label={adminRecordStatusLabel(k, r.status || '')} />
        </td>
        <td>{r.capabilities || '—'}</td>
        <td>{datetime(r.created_at)}</td>
      </tr>
    );
  if (k === 'split-transactions')
    return (
      <tr>
        <td>
          <strong>{r.merchant_split_no || r.id.slice(-8)}</strong>
          <span className="sub-cell">{r.external_split_no || '待渠道受理'}</span>
        </td>
        <td>
          {r.provider}
          <span className="sub-cell">{r.sub_merchant_id || '—'}</span>
        </td>
        <td>{money(r.platform_amount_minor || 0)}</td>
        <td>{money(r.provider_amount_minor || 0)}</td>
        <td>
          <Status value={r.status || ''} label={adminRecordStatusLabel(k, r.status || '')} />
        </td>
        <td>{datetime(r.created_at)}</td>
        <td>
          {finance && (r.status === 'ready' || r.status === 'failed') && (
            <ActionDialog
              title="提交分账"
              intent="financial"
              submitLabel="确认执行分账"
              trigger={r.status === 'failed' ? '重试分账' : '执行分账'}
              path={`/admin/split-transactions/${r.id}/execute`}
              description="提交后如果渠道响应不确定，将进入待查单状态，禁止直接重复发起。"
              fields={[]}
            />
          )}
          {finance && r.status === 'pending' && (
            <ActionDialog
              title="刷新分账状态"
              intent="financial"
              submitLabel="立即查单"
              trigger="查单"
              path={`/admin/split-transactions/${r.id}/refresh`}
              description="向支付渠道主动查询该分账单的最终状态。"
              fields={[]}
            />
          )}
        </td>
      </tr>
    );
  if (k === 'refunds')
    return (
      <tr>
        <td>
          <strong>{r.merchant_refund_no || r.id.slice(-8)}</strong>
          <span className="sub-cell">{r.external_refund_no || '—'}</span>
        </td>
        <td>{money(r.amount_minor || 0)}</td>
        <td>
          {money(r.platform_amount_minor || 0)} / {money(r.provider_amount_minor || 0)}
        </td>
        <td>
          <Status value={r.status || ''} label={adminRecordStatusLabel(k, r.status || '')} />
        </td>
        <td>{r.reason || '—'}</td>
        <td>
          {finance && r.status === 'manual_review' && (
            <ActionDialog
              title="记录线下退款"
              intent="financial"
              submitLabel="确认记录退款"
              trigger="记录退款"
              path={`/admin/refunds/${r.id}/manual`}
              fields={[{ name: 'reference', label: '实际退款凭证编号' }]}
            />
          )}
          {finance && (r.status === 'ready' || r.status === 'failed') && (
            <ActionDialog
              title="执行在线退款"
              intent="financial"
              submitLabel="确认执行退款"
              trigger={r.status === 'failed' ? '重试退款' : '执行退款'}
              path={`/admin/refunds/${r.id}/execute`}
              description={
                r.provider === 'alipay'
                  ? '支付宝会在同一退款请求中处理退款与退分账，请核对退款总额及平台佣金。'
                  : '已分账付款会要求先完成对应分账回退。'
              }
              fields={[]}
            />
          )}
          {finance && r.status === 'pending' && (
            <ActionDialog
              title="刷新退款状态"
              intent="financial"
              submitLabel="立即查单"
              trigger="查单"
              path={`/admin/refunds/${r.id}/refresh`}
              fields={[]}
            />
          )}
        </td>
      </tr>
    );
  if (k === 'split-reversals')
    return (
      <tr>
        <td>
          <strong>{r.merchant_reversal_no || r.id.slice(-8)}</strong>
          <span className="sub-cell">{r.external_reversal_no || '—'}</span>
        </td>
        <td className="small">{r.split_transaction_id?.slice(-8)}</td>
        <td>
          {money(r.platform_amount_minor || 0)} / {money(r.provider_amount_minor || 0)}
        </td>
        <td>
          <Status value={r.status || ''} label={adminRecordStatusLabel(k, r.status || '')} />
        </td>
        <td>{datetime(r.created_at)}</td>
        <td>
          {r.provider === 'alipay' && (r.status === 'ready' || r.status === 'failed') && (
            <Link className="link" to="/refunds">
              随退款一并处理
            </Link>
          )}
          {finance &&
            r.provider !== 'alipay' &&
            (r.status === 'ready' || r.status === 'failed') && (
              <ActionDialog
                title="执行分账回退"
                intent="financial"
                submitLabel="确认分账回退"
                trigger={r.status === 'failed' ? '重试回退' : '执行回退'}
                path={`/admin/split-reversals/${r.id}/execute`}
                fields={[]}
              />
            )}
          {finance && r.status === 'pending' && (
            <ActionDialog
              title="刷新分账回退"
              intent="financial"
              submitLabel="立即查单"
              trigger="查单"
              path={`/admin/split-reversals/${r.id}/refresh`}
              fields={[]}
            />
          )}
        </td>
      </tr>
    );
  if (k === 'reconciliation')
    return (
      <tr>
        <td>
          <strong>
            {r.provider} · {r.record_type}
          </strong>
          <span className="sub-cell">{r.external_record_no || '—'}</span>
          <span className="sub-cell">{r.source_reference || '—'}</span>
        </td>
        <td>{r.business_date || '—'}</td>
        <td>
          {money(r.expected_amount_minor || 0)} / {money(r.provider_amount_minor || 0)}
        </td>
        <td>
          {money(r.fee_amount_minor || 0)} / {money(r.difference_minor || 0)}
        </td>
        <td>
          <Status value={r.status || ''} label={adminRecordStatusLabel(k, r.status || '')} />
          {r.resolution && <span className="sub-cell">{r.resolution}</span>}
        </td>
        <td>
          {finance && r.status !== 'matched' && r.status !== 'resolved' && (
            <ActionDialog
              title="处理对账差异"
              intent="financial"
              submitLabel="确认处理差异"
              trigger="标记已处理"
              path={`/admin/reconciliation/${r.id}/resolve`}
              fields={[
                {
                  name: 'resolution',
                  label: '处理说明',
                  type: 'textarea',
                  min: 2,
                },
              ]}
            />
          )}
        </td>
      </tr>
    );
  if (k === 'payments' || k === 'settlements')
    return (
      <tr>
        <td>{r.id.slice(-8)}</td>
        <td>
          <strong>{money(r.amount_minor || 0)}</strong>
        </td>
        <td>
          {r.reference || '—'}
          {k === 'payments' && r.reason && (
            <span className="sub-cell">
              {r.status === 'rejected' ? '退回原因：' : '核验说明：'}
              {r.reason}
            </span>
          )}
        </td>
        <td>
          <Status value={r.status || ''} />
        </td>
        <td>{datetime(r.created_at)}</td>
        <td>
          {finance && k === 'payments' && r.status === 'confirmed' && (
            <ActionDialog
              title="发起退款"
              intent="financial"
              submitLabel="确认发起退款"
              trigger="退款"
              path={`/admin/payments/${r.id}/refunds`}
              description="按实际退款金额创建退款。在线支付如已分账，会先生成分账回退任务。"
              fields={[
                { name: 'amount', label: '退款金额（元）', type: 'number', min: 0.01, step: 0.01 },
                { name: 'reason', label: '退款原因', type: 'textarea', min: 2 },
              ]}
              transform={(v) => ({
                amount_minor: Math.round(Number(v.amount) * 100),
                reason: v.reason,
              })}
            />
          )}
          {finance && k === 'payments' && r.status === 'pending_review' && (
            <ActionDialog
              title="确认付款已到账"
              intent="financial"
              submitLabel="确认付款到账"
              trigger="审核到账"
              path={`/admin/payments/${r.id}/confirm`}
              description="请实际核对金额和收款凭证后确认。首款确认后启动履约；尾款确认后完成订单。"
              fields={[{ name: 'reason', label: '核验依据与说明', type: 'textarea', min: 2 }]}
            />
          )}
          {finance && k === 'payments' && r.status === 'pending_review' && !r.payment_intent_id && (
            <ActionDialog
              title="退回线下付款凭证"
              trigger="退回凭证"
              path={`/admin/payments/${r.id}/reject`}
              intent="destructive"
              submitLabel="确认退回凭证"
              description="仅退回尚未确认到账的线下凭证，付款阶段恢复待支付。客户可更正后重新提交；该操作不执行退款。"
              fields={[
                { name: 'reason', label: '具体退回原因', type: 'textarea', min: 5, max: 500 },
              ]}
            />
          )}
          {finance && k === 'settlements' && r.status === 'eligible' && (
            <ActionDialog
              title="记录已完成结算"
              intent="financial"
              submitLabel="确认记录结算"
              trigger="记录结算"
              path={`/admin/settlements/${r.id}/settle`}
              description="请确认已通过正规渠道向服务商支付后填写真实凭证。"
              fields={[
                { name: 'reference', label: '实际付款凭证编号' },
                { name: 'reason', label: '结算说明', type: 'textarea', min: 2 },
              ]}
            />
          )}
        </td>
      </tr>
    );
  if (k === 'cms')
    return (
      <tr>
        <td>{r.key}</td>
        <td>{r.title}</td>
        <td>{r.content}</td>
        <td>{r.link || '—'}</td>
        <td>{r.enabled ? '启用' : '停用'}</td>
      </tr>
    );
  return (
    <tr>
      <td>
        <span className="title-cell">{r.title}</span>
        <span className="sub-cell">{r.id.slice(-8)}</span>
      </td>
      <td>{r.category}</td>
      <td>
        <Status value={r.status || ''} />
      </td>
      <td>{String(r.deadline || '—')}</td>
      <td>{datetime(r.created_at)}</td>
    </tr>
  );
}
